import React, { useEffect, useState, useRef } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { startReleaseMonitor, safeToPromptForUpdate } from "@/lib/releaseMonitor";
import { activateWaitingWorker } from '@/lib/workerUpdate';

export default function AppUpdateGate() {
  const [releaseState, setReleaseState] = useState({state:"unknown",latest:null,connected:false});
  const [registration, setRegistration] = useState(null);
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [updateError, setUpdateError] = useState(false);
  const cleanup = useRef(null);
  const pending = useRef(false);
  useEffect(() => () => cleanup.current?.(), []);

  useEffect(() => {
    let alive = true;
    const showWhenSafe = () => { if (pending.current && safeToPromptForUpdate()) { pending.current = false; setOpen(true); } };
    const onReady = (e) => { if (!alive) return; setRegistration(e.detail?.registration || null); pending.current = true; showWhenSafe(); };
    const onStatus = (event) => { if (alive) setReleaseState(event.detail); };
    window.addEventListener("slx-release-status", onStatus);
    document.documentElement.classList.add("slx-live-version");
    window.addEventListener("slx-app-update-ready", onReady);
    if (window.__slxWaitingRegistration?.waiting) onReady({ detail: { registration: window.__slxWaitingRegistration } });
    navigator.serviceWorker?.getRegistration?.().then((reg) => { if (reg?.waiting) onReady({ detail: { registration: reg } }); }).catch(() => {});
    const stopMonitor = startReleaseMonitor();
    const timer = setInterval(showWhenSafe, 1000);
    return () => { alive = false; stopMonitor(); clearInterval(timer); window.removeEventListener("slx-app-update-ready", onReady); window.removeEventListener("slx-release-status", onStatus); document.documentElement.classList.remove("slx-live-version"); };
  }, []);

  const apply = async () => {
    if (!safeToPromptForUpdate()) { setUpdateError(true); return; }
    setRefreshing(true);
    setUpdateError(false);
    let reg;
    try { reg = registration || await navigator.serviceWorker?.getRegistration?.(); }
    catch { setRefreshing(false); setUpdateError(true); return; }
    if (reg?.installing && !reg.waiting) { setRefreshing(false); setUpdateError(true); return; }
    const waiting = reg?.waiting;
    if (!waiting) { window.location.reload(); return; }
    cleanup.current?.();
    cleanup.current = activateWaitingWorker(navigator.serviceWorker, waiting, () => window.location.reload(),
      () => { setRefreshing(false); setUpdateError(true); });
  };

  const indicatorState = registration?.waiting ? "update" : releaseState.state;
  const versionLabel = releaseState.latest?.version ? `v${releaseState.latest.version}` : "Checking version…";
  return <><button type="button" className={`slx-live-version-pill slx-live-version-pill--${indicatorState}`} data-testid="live-version-indicator" aria-label={`${versionLabel}: ${indicatorState === "update" ? "Update ready" : indicatorState === "current" ? "Up to date" : "Version unverified"}`}
    title={`${indicatorState === "update" ? "Update ready" : indicatorState === "current" ? "Up to date" : "Connection/version unverified"} · ${releaseState.connected ? "Live connection" : "Fallback checks"} · Running ${releaseState.currentRelease || "unknown"} · Latest ${releaseState.latest?.releaseID || "unknown"}`}
    onClick={() => { if (indicatorState === "update") { pending.current = true; if (safeToPromptForUpdate()) { pending.current = false; setOpen(true); } } }}>
    <span className="slx-live-version-dot" aria-hidden="true"/>{versionLabel}{indicatorState === "update" ? " · update" : ""}
  </button><Dialog open={open} onOpenChange={() => { /* intentionally user-confirmed only */ }}>
    <DialogContent className="sm:max-w-md border-accent/30" hideClose>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl flex items-center gap-3"><Sparkles className="h-5 w-5 text-accent"/>SLX-one update ready</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground leading-relaxed">A small update is required to keep this device synchronized with the vessel platform. Your signed-in session and cabin/device assignment will remain active.</p>
        {updateError && <p role="alert" className="text-sm text-amber-600">The update has not activated yet. Check the connection and try again.</p>}
        <Button className="w-full" onClick={apply} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}/>{refreshing ? "Refreshing…" : "Refresh now"}
        </Button>
      </div>
    </DialogContent>
  </Dialog></>;
}
